import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Trash2, ExternalLink } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export const BillsManager = () => {
  const qc = useQueryClient();
  const { data: bills = [] } = useQuery({ queryKey: ["admin-bills"], queryFn: () => api.get("/admin/bills").then((r) => r.data) });
  const [q, setQ] = useState("");
  const s = q.toLowerCase();
  const rows = bills.filter((b) => !s || b.title.toLowerCase().includes(s) || b.electorate.toLowerCase().includes(s) || b.member_name.toLowerCase().includes(s));

  const del = async (b) => {
    if (!window.confirm(`Delete "${b.title}" from ${b.electorate}?`)) return;
    try {
      await api.delete(`/admin/bills/${b.id}`);
      toast.success("Entry deleted");
      qc.invalidateQueries({ queryKey: ["admin-bills"] });
      qc.invalidateQueries({ queryKey: ["bills", b.electorate] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="rounded-2xl bg-[#1B2A41] border border-[#2C3E55] overflow-hidden">
      <div className="p-4 flex items-center gap-2 bg-[#121E2B]/40 border-b border-[#2C3E55]">
        <div className="flex items-center gap-2 flex-1 bg-[#121E2B] rounded-full px-4 h-10 border border-[#2C3E55]">
          <Search className="w-4 h-4 text-[#8FA58A]" />
          <input data-testid="admin-bills-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, electorate or member" className="flex-1 bg-transparent outline-none text-sm" />
        </div>
        <span className="text-xs text-[#F8FAFC]/60" data-testid="admin-bills-count">{bills.length} entries</span>
      </div>
      <div className="overflow-x-auto">
        <table data-testid="admin-bills-table" className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-[#8FA58A] bg-[#121E2B]">
            <tr>{["Type", "Title", "Electorate", "Status", "Logged by", "When", ""].map((h) => <th key={h} className="px-4 py-3 font-type">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#F8FAFC]/50" data-testid="admin-bills-empty">No bills or votes logged yet</td></tr>
            ) : rows.map((b) => (
              <tr key={b.id} data-testid={`admin-bill-row-${b.id}`} className="border-t border-[#2C3E55]/60 hover:bg-[#121E2B]/60">
                <td className="px-4 py-3 font-type uppercase text-[11px]">{b.type}</td>
                <td className="px-4 py-3 font-semibold max-w-md">
                  <span className="line-clamp-2">{b.title}</span>
                  {b.link && <a href={b.link} target="_blank" rel="noreferrer" className="text-[11px] text-[#8FA58A] hover:text-[#E2705A] inline-flex items-center gap-1 mt-0.5"><ExternalLink className="w-3 h-3" /> source</a>}
                </td>
                <td className="px-4 py-3">{b.electorate}</td>
                <td className="px-4 py-3 text-[#F8FAFC]/75">{b.status}</td>
                <td className="px-4 py-3 text-[#F8FAFC]/75">{b.member_name}</td>
                <td className="px-4 py-3 text-[#F8FAFC]/60 whitespace-nowrap">{new Date(b.created_at).toLocaleDateString("en-AU")}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => del(b)} data-testid="admin-bill-delete-button" className="p-2 rounded-lg text-[#F8FAFC]/60 hover:text-[#E2705A] hover:bg-[#E2705A]/10"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
